import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import type { Event } from "@/react/types/Event";
import { eventApi } from "@circle-react/api/eventApi";
import type { RecurringEditMode } from "@circle-react/components/Events/helpers/constants";
import { useInvalidateEventQueries } from "@circle-react/components/Events/hooks/useInvalidateEventQueries";
import { eventDeletionMessage } from "@circle-react/components/EventsV3/Modal/utils";
import { isRecurringFreeEvent } from "@circle-react/components/EventsV3/Modal/utils/helpers";
import { useEditEventModal } from "@circle-react/components/Modals/EditEventModal";
import { usePunditUserContext } from "@circle-react/contexts";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import { useToast } from "@circle-react-uikit/ToastV2";

interface EventPolicies {
  can_destroy_post: boolean;
  can_duplicate_post: boolean;
  can_update_post: boolean;
}

type EventWithPolicies = Event & {
  policies?: EventPolicies;
};

interface UseEventMenuProps {
  event: EventWithPolicies;
  onDestroy?: (eventId: number) => void;
}

export const useEventMenu = ({ event, onDestroy }: UseEventMenuProps) => {
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const invalidateQueries = useInvalidateEventQueries();
  const editEventModal = useEditEventModal();
  const { policies } = event;
  const {
    can_destroy_post: canDelete,
    can_update_post: canEdit,
    can_duplicate_post: canDuplicate,
  } = policies || {};
  const isPinned = event.pinned_at_top_of_space;
  const { success, error } = useToast();
  const [isDuplicateEventModalOpen, toggleDuplicateEventModal] =
    useBoolean(false);
  const [isConfirmationModalOpen, toggleConfirmationModal] = useBoolean(false);
  const [isRecurringModalOpen, toggleRecurringModal] = useBoolean(false);

  const onEdit = () => {
    if (!canEdit || isViewOnlyMasquerading) {
      return;
    }
    void editEventModal.show({ event } as any);
  };

  const onDuplicate = toggleDuplicateEventModal;

  const deleteMutation = useMutation(
    (deleteMode?: RecurringEditMode | "") =>
      eventApi.destroy({ event: event, params: { delete_mode: deleteMode } }),
    {
      onSuccess: () => {
        onDestroy?.(event.id);
        success(eventDeletionMessage(event));
        invalidateQueries();
      },
      onError: () => error(t("event_dropdown_actions.delete_failed")),
    },
  );

  const onDeleteClickHandler = () => {
    if (isViewOnlyMasquerading) {
      return;
    }
    if (isRecurringFreeEvent(event)) {
      toggleRecurringModal();
    } else {
      toggleConfirmationModal();
    }
  };

  const onPinToTop = async () => {
    const response = (await eventApi.pin<Response>({
      event,
      formData: { pin_to_top: true, is_pinned: !isPinned },
    })) as Response;

    if (response.ok) {
      success(t(isPinned ? "event_unpinned" : "event_pinned"));
      invalidateQueries({ shouldInvalidateHomePagePosts: true });
    } else {
      error(t(isPinned ? "event_unpinned_error" : "event_pinned_error"));
    }
  };

  return {
    canEdit,
    onEdit,
    canDelete,
    canDuplicate,
    onDelete: deleteMutation.mutate,
    isDeleting: deleteMutation.isLoading,
    onPinToTop,
    onDuplicate,
    onDeleteClickHandler,
    isDuplicateEventModalOpen,
    isConfirmationModalOpen,
    toggleConfirmationModal,
    isRecurringModalOpen,
    toggleRecurringModal,
  };
};
